// Pure helpers for edits suggested to approved vendors and reps (Nov 2026).
// No Firebase imports. An edit stores only the fields that change; '' means
// "clear it", the same convention as a moderator's own edit (VendorPatch).

import { isNationalVendor } from './vendors';
import type { RepEditChanges, Vendor, VendorEdit, VendorEditChanges, VendorRep } from '../types';

export interface VendorDraft {
  organization: string;
  website: string;
  categories: string[];
  /** Where the vendor works. Never shown by name: areas are backend-only. */
  scope: 'here' | 'everywhere';
  licenses: string;
  notes: string;
}

export interface RepDraft {
  name: string;
  role: string;
  email: string;
  phone: string;
}

export function vendorDraft(vendor: Vendor): VendorDraft {
  return {
    organization: vendor.organization,
    website: vendor.website ?? '',
    categories: vendor.categories,
    scope: isNationalVendor(vendor) ? 'everywhere' : 'here',
    licenses: vendor.licenses ?? '',
    notes: vendor.notes ?? '',
  };
}

export function repDraft(rep: VendorRep): RepDraft {
  return { name: rep.name, role: rep.role ?? '', email: rep.email ?? '', phone: rep.phone ?? '' };
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x, i) => x.toLowerCase() === b[i].toLowerCase());

/**
 * What changed between the vendor and the draft. `areaId` is where the person
 * is looking; it becomes the vendor's area only if they say the vendor works
 * here rather than everywhere, and only if that differs from today.
 */
export function vendorChanges(
  vendor: Vendor,
  draft: VendorDraft,
  areaId: string | null
): VendorEditChanges {
  const before = vendorDraft(vendor);
  const out: VendorEditChanges = {};
  const organization = draft.organization.trim();
  if (organization && organization !== before.organization) out.organization = organization;
  for (const key of ['website', 'licenses', 'notes'] as const) {
    const value = draft[key].trim();
    if (value !== before[key].trim()) out[key] = value;
  }
  if (!sameList(draft.categories, before.categories)) out.categories = draft.categories;
  if (draft.scope !== before.scope) {
    if (draft.scope === 'everywhere') out.areaIds = ['*'];
    else if (areaId) out.areaIds = [areaId];
  }
  return out;
}

export function repChanges(rep: VendorRep, draft: RepDraft): RepEditChanges {
  const before = repDraft(rep);
  const out: RepEditChanges = {};
  const name = draft.name.trim();
  if (name && name !== before.name) out.name = name;
  for (const key of ['role', 'email', 'phone'] as const) {
    const value = draft[key].trim();
    if (value !== before[key].trim()) out[key] = value;
  }
  return out;
}

export interface EditLine {
  label: string;
  before: string;
  after: string;
}

const LABELS: Record<string, string> = {
  organization: 'Name',
  website: 'Website',
  categories: 'Kinds of work',
  areaIds: 'Works',
  serviceAreaText: 'Service area notes',
  licenses: 'Licenses',
  notes: 'Notes',
  name: 'Name',
  role: 'Role',
  email: 'Email',
  phone: 'Phone',
};

const ORDER = Object.keys(LABELS);

function show(key: string, value: unknown): string {
  if (key === 'areaIds') {
    return Array.isArray(value) && value.includes('*') ? 'Everywhere' : 'Locally';
  }
  if (Array.isArray(value)) return value.join(', ') || '(none)';
  return typeof value === 'string' && value.trim() ? value : '(empty)';
}

/** One line per changed field, current value first, for the review queue. */
export function describeEdit(
  edit: Pick<VendorEdit, 'changes' | 'repId'>,
  vendor: Vendor | undefined,
  rep: VendorRep | undefined
): EditLine[] {
  const current = (edit.repId ? rep : vendor) as Record<string, unknown> | undefined;
  return Object.entries(edit.changes)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => ORDER.indexOf(a) - ORDER.indexOf(b))
    .map(([key, value]) => ({
      label: LABELS[key] ?? key,
      before: show(key, current?.[key]),
      after: show(key, value),
    }));
}

/** Edits still waiting for a moderator. */
export function pendingEditCount(edits: Pick<VendorEdit, 'status'>[]): number {
  return edits.filter((e) => e.status === 'suggested').length;
}
